import React, { useEffect, useState } from "react";
import styled from "styled-components";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import AdminShell from "./AdminShell";
import { firestore } from "../firebase";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";

/* ---------- UI (nya designen) ---------- */

const Grid = styled.div`
  display: grid;
  gap: 14px;
  grid-template-columns: 1.1fr 0.9fr;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 18px;
  padding: 14px;
  background: #fff;
  box-shadow: 0 12px 34px rgba(0, 0, 0, 0.06);
`;

const Title = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 950;
  color: #111827;
`;

const Muted = styled.div`
  margin-top: 8px;
  color: #6b7280;
  font-weight: 800;
  font-size: 13px;
  line-height: 1.5;
`;

const BtnRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 12px;
`;

const Btn = styled.button`
  appearance: none;
  border: 1px solid rgba(15, 118, 110, 0.22);
  background: #0f766e;
  color: #fff;
  font-weight: 950;
  border-radius: 999px;
  padding: 10px 14px;
  cursor: pointer;

  &:disabled {
    opacity: 0.65;
    cursor: wait;
    transform: none;
  }

  &:hover {
    filter: brightness(1.03);
    transform: translateY(-1px);
  }
`;

const Ghost = styled(Btn)`
  background: #fff;
  color: #111827;
  border: 1px solid #e5e7eb;

  &:hover {
    background: #f9fafb;
  }
`;

const PreviewBox = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 14px;
  background: #fafafa;
`;

const PreviewHtml = styled.div`
  color: #111827;
  font-weight: 700;
  line-height: 1.65;

  /* lite snyggare html i preview */
  p {
    margin: 0 0 10px;
  }
  a {
    color: #0f766e;
    font-weight: 900;
  }
  h1, h2, h3 {
    margin: 10px 0 10px;
  }
  ul, ol {
    margin: 8px 0 8px 18px;
  }
`;

/* Quill wrapper: lite “nya design”-känsla */
const QuillWrap = styled.div`
  margin-top: 12px;

  .ql-toolbar.ql-snow {
    border-radius: 16px 16px 0 0;
    border-color: #e5e7eb;
    background: #fff;
  }

  .ql-container.ql-snow {
    border-radius: 0 0 16px 16px;
    border-color: #e5e7eb;
    min-height: 260px;
    background: #fff;
  }

  .ql-editor {
    font-weight: 700;
    color: #111827;
    line-height: 1.65;
  }
`;

/* ---------- Component ---------- */

export default function AdminAvgift() {
  const DOC_REF = doc(firestore, "avgifter", "general"); // behåller din struktur

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [avgift, setAvgift] = useState("");
  const [editorValue, setEditorValue] = useState("");
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    const fetchAvgift = async () => {
      setLoading(true);
      try {
        const docSnap = await getDoc(DOC_REF);
        if (docSnap.exists()) {
          const value = docSnap.data()?.avgift || "";
          setAvgift(value);
          setEditorValue(value);
        } else {
          setAvgift("");
          setEditorValue("");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchAvgift();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleEdit = () => {
    // om du klickar “Redigera” -> fyll editorn från senaste sparade
    if (!isEditing) setEditorValue(avgift || "");
    setIsEditing((v) => !v);
  };

  const handleSave = async () => {
    setBusy(true);
    try {
      await setDoc(
        DOC_REF,
        {
          avgift: editorValue,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      setAvgift(editorValue);
      setIsEditing(false);
      alert("Sparat!");
    } catch (e) {
      alert(e?.message || "Kunde inte spara avgifter.");
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = () => {
    setEditorValue(avgift || "");
    setIsEditing(false);
  };

  return (
    <AdminShell title="Avgifter">
      <Grid>
        <Card>
          <Title>Redigera text</Title>
          <Muted>
            Lagrar i Firestore: <code>avgifter/general</code> → fältet <b>avgift</b>.
          </Muted>

          {loading ? (
            <Muted style={{ marginTop: 12 }}>Laddar…</Muted>
          ) : (
            <>
              {isEditing ? (
                <QuillWrap>
                  <ReactQuill
                    value={editorValue}
                    onChange={setEditorValue}
                    theme="snow"
                    modules={{
                      toolbar: [
                        [{ header: "1" }, { header: "2" }, { font: [] }],
                        [{ list: "ordered" }, { list: "bullet" }],
                        ["bold", "italic", "underline"],
                        ["link"],
                        ["blockquote"],
                        [{ align: [] }],
                        ["image"],
                        ["clean"],
                      ],
                    }}
                  />
                </QuillWrap>
              ) : (
                <PreviewBox>
                  {avgift ? (
                    <PreviewHtml dangerouslySetInnerHTML={{ __html: avgift }} />
                  ) : (
                    <Muted>Ingen text sparad ännu.</Muted>
                  )}
                </PreviewBox>
              )}

              <BtnRow>
                {isEditing ? (
                  <>
                    <Btn type="button" onClick={handleSave} disabled={busy}>
                      {busy ? "Sparar…" : "Spara ändringar"}
                    </Btn>
                    <Ghost type="button" onClick={handleCancel} disabled={busy}>
                      Avbryt
                    </Ghost>
                  </>
                ) : (
                  <Btn type="button" onClick={toggleEdit} disabled={loading}>
                    Redigera
                  </Btn>
                )}
              </BtnRow>
            </>
          )}
        </Card>

        <Card>
          <Title>Tips</Title>
          <Muted>
            • Du kan klistra in text från Word/Facebook men det kan följa med “skräp-html”.  
            <br />
            • Vill du ha renare HTML kan vi lägga in en “Rensa formatering”-knapp som tvättar HTML innan spar.
            <br />
            • Om du vill följa samma upplägg som News (med bild upload) kan vi lägga till fältet <b>image1</b> här också.
          </Muted>
        </Card>
      </Grid>
    </AdminShell>
  );
}
